import React, { useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import { useTranslation } from "react-i18next";
import { localizeDigitsInElement } from "../../helpers/language_helper";

/**
 * Drop-in replacement for `import DataTable from "react-data-table-component"`.
 *
 * react-data-table-component renders its own pagination footer ("Rows per
 * page:", "1-4 of 4") and its own empty state ("There are no records to
 * display"). Those strings live inside the library, so they stay English
 * unless they are passed in explicitly. This wrapper supplies the localized
 * versions for every table in the app from one place.
 *
 * Anything a caller passes explicitly wins, so existing per-table overrides
 * keep working.
 *
 * NOTE: `direction` is deliberately NOT forced to "rtl" for Arabic. Doing so
 * makes react-data-table-component mirror the whole table, which reverses the
 * column order. The column sequence must stay identical in both languages -
 * only the text inside the table is translated.
 *
 * The pagination footer's numbers ("1-4 of 4", the rows-per-page dropdown
 * value) are computed and rendered entirely inside the library with no prop
 * to run our own formatter over them, so a MutationObserver on the rendered
 * `.rdt_Pagination` node swaps Western digits for Arabic-Indic ones after
 * every update, in Arabic only. This only rewrites digit characters in text
 * nodes - it never touches the library's own state/handlers, so paging,
 * sorting, and the rows-per-page control keep working exactly as before.
 */
const LocalizedDataTable = (props) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);

  const { paginationComponentOptions, noDataComponent, ...rest } = props;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const applyDigits = () => {
      const pagination = container.querySelector(".rdt_Pagination");
      if (pagination) localizeDigitsInElement(pagination);
    };

    applyDigits();

    const observer = new MutationObserver(applyDigits);
    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, [t]);

  return (
    <div ref={containerRef}>
      <DataTable
        {...rest}
        noDataComponent={
          noDataComponent !== undefined
            ? noDataComponent
            : t("There are no records to display")
        }
        paginationComponentOptions={{
          rowsPerPageText: t("Rows per page:"),
          rangeSeparatorText: t("of"),
          selectAllRowsItemText: t("All"),
          ...(paginationComponentOptions || {}),
        }}
      />
    </div>
  );
};

export default LocalizedDataTable;
